import { useMemo } from 'react';
import { enUS, es } from 'date-fns/locale';
import { DateRangePicker } from 'src/modules/shared/components/molecules/date-range-picker';
import type {
  CalendarDateRangePickerLabels,
  CalendarDateRangeSelection,
} from 'src/modules/shared/types/date-range.type';
import {
  findMatchingCalendarDateRangePreset,
  formatCalendarDateRange,
  toCalendarDateToken,
} from 'src/modules/shared/utils/date-range.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  ExecutionDatePreset,
  type ExecutionCalendarDateRange,
  type ExecutionDatePresetId,
} from 'src/modules/automations/types/executions/execution-date-filter.type';
import { buildExecutionDatePresets } from 'src/modules/automations/utils/executions/execution-date-filter.util';

interface ExecutionDateRangePickerProps {
  /** Applied route-level execution date range. */
  value: ExecutionCalendarDateRange;
  /** Applies one complete date range. */
  onApply: (value: Required<ExecutionCalendarDateRange>) => void;
}

/** Execution-domain adapter around the shared responsive date-range picker. */
export const ExecutionDateRangePicker: React.FC<
  ExecutionDateRangePickerProps
> = ({ value, onApply }) => {
  const { language, t } = useI18n();
  const now = useMemo(() => new Date(), []);
  const presets = useMemo(
    () =>
      buildExecutionDatePresets(
        {
          [ExecutionDatePreset.TODAY]: t(
            'automations.aiAgents.executions.dateRange.today'
          ),
          [ExecutionDatePreset.LAST_7_DAYS]: t(
            'automations.aiAgents.executions.dateRange.last7Days'
          ),
          [ExecutionDatePreset.LAST_30_DAYS]: t(
            'automations.aiAgents.executions.dateRange.last30Days'
          ),
        },
        now
      ),
    [now, t]
  );
  const labels = useMemo<CalendarDateRangePickerLabels>(
    () => ({
      trigger: t('automations.aiAgents.executions.dateRange.trigger'),
      title: t('automations.aiAgents.executions.dateRange.title'),
      presets: t('automations.aiAgents.executions.dateRange.presets'),
      from: t('automations.aiAgents.executions.dateRange.from'),
      to: t('automations.aiAgents.executions.dateRange.to'),
      cancel: t('shared.actions.cancel'),
      close: t('automations.aiAgents.executions.dateRange.close'),
      apply: t('shared.actions.apply'),
      missingError: t('automations.aiAgents.executions.dateRange.missing'),
      invalidError: t('automations.aiAgents.executions.dateRange.invalid'),
      orderError: t('automations.aiAgents.executions.dateRange.order'),
      beforeMinError: t('automations.aiAgents.executions.dateRange.beforeMin'),
      afterMaxError: t('automations.aiAgents.executions.dateRange.afterMax'),
    }),
    [t]
  );
  const selectedPresetId =
    findMatchingCalendarDateRangePreset(value, presets)?.id ?? null;
  const today = toCalendarDateToken(now);

  const handleApply = (
    selection: CalendarDateRangeSelection<ExecutionDatePresetId>
  ) => onApply(selection.value);

  return (
    <DateRangePicker
      value={value}
      selectedPresetId={selectedPresetId}
      presets={presets}
      labels={labels}
      onApply={handleApply}
      bounds={{ maxDate: today }}
      calendarLocale={language === 'es-CL' ? es : enUS}
      locale={language}
      className="w-full sm:w-auto"
      triggerClassName="w-full sm:min-w-64"
      formatValue={(range) =>
        range.from && range.to
          ? formatCalendarDateRange(range, language)
          : t('automations.aiAgents.executions.dateRange.all')
      }
    />
  );
};
